import qs from 'qs';

import { api, embedApi, publicApi } from '../../api/api';

export const findSummaryRanking = async (market) => {
  const query = qs.stringify(
    {
      populate: {
        button1: true,
        button2: true,
        [market]: {
          populate: '*',
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const {
    data: { data },
  } = await publicApi.get(`/rank?${query}`);
  return data;
};

export const findRankingFormSheet = async () => {
  const query = qs.stringify(
    {
      populate: ['rankFormSheet'],
    },
    {
      encodeValuesOnly: true,
    },
  );
  const {
    data: { data },
  } = await publicApi.get(`/rank?${query}`);
  return data;
};

export const findFilters = async (institutionId = undefined, isLoggedIn) => {
  const query = qs.stringify(
    {
      institutionId,
    },
    {
      encodeValuesOnly: true,
    },
  );
  if (isLoggedIn) {
    const { data } = await api.get(`/rankings-filters/all?${query}`);
    return data;
  } else {
    const { data } = await publicApi.get(`/rankings-filters/all?${query}`);
    return data;
  }
};

/**
 * Retorna a estrutura completa e aninhada dos filtros de ranking:
 *   market → rankings[].type → criterias[].code → years[].year → timeranges[]
 *
 * Diferente de `findFilters`, este endpoint retorna anos e timeranges aninhados
 * dentro de cada critério, permitindo carregar todas as opções de filtro em uma
 * única requisição.
 */
export const findAllFilters = async (institutionId = undefined, isLoggedIn) => {
  const query = qs.stringify({ institutionId }, { encodeValuesOnly: true });
  const client = isLoggedIn ? api : publicApi;
  const { data } = await client.get(`/rankings-filters/all?${query}`);
  return data;
};

/**
 * Helpers para navegar na estrutura aninhada retornada por findAllFilters.
 * Suportam tanto o novo formato (rankings.type) quanto o legado (rankings.code).
 */

export const getRankingEntry = (filtersData, market, rankingCode) =>
  filtersData?.[market]?.rankings?.find(
    (r) => (r.type ?? r.code) === rankingCode,
  ) ?? null;

export const getCriteriaEntry = (rankingEntry, criteriaCode) =>
  rankingEntry?.criterias?.find((c) => c.code === criteriaCode) ?? null;

export const getYearEntry = (criteriaEntry, year) => {
  const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;
  return (
    criteriaEntry?.years?.find((y) => y.year === yearNum || y.year === year) ??
    null
  );
};

export const getFirstYearFromFilters = (filtersData, market) =>
  filtersData?.[market]?.years?.[0] ??
  filtersData?.[market]?.rankings?.[0]?.criterias?.[0]?.years?.[0]?.year ??
  null;

export const getRankingHasConsolidated = (filtersData, market, rankingCode) => {
  const ranking = getRankingEntry(filtersData, market, rankingCode);
  if (!ranking) return false;
  if (typeof ranking.hasConsolidated === 'boolean')
    return ranking.hasConsolidated;
  return (
    ranking.criterias?.some((c) =>
      c.years?.some((y) => y.timeranges?.some((t) => t === 'consolidated')),
    ) ?? false
  );
};

export const getRankingMethodology = (filtersData, market, rankingCode) =>
  getRankingEntry(filtersData, market, rankingCode)?.methodology ?? null;

export const findRanking = async ({
  filters,
  isLoggedIn,
  widgetKey,
  page = null,
  pageSize = null,
  sortBy = 'position',
  sortOrder = 'asc',
}) => {
  const query = qs.stringify(
    {
      ...filters,
      page,
      pageSize,
      sortBy,
      sortOrder,
    },
    {
      encodeValuesOnly: true,
    },
  );
  if (isLoggedIn) {
    const { data } = await api.get(`/rankings?${query}`);
    return data;
  } else if (widgetKey) {
    const { data } = await embedApi.get(`/rankings?${query}`);
    return data;
  } else {
    const { data } = await publicApi.get(`/rankings?${query}`);
    return data;
  }
};

export const findStatisticsRanking = async (filters) => {
  const query = qs.stringify(
    {
      ...filters,
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/statistics-ranking?${query}`);

  return data;
};

export const findRankingStatisticsResume = async (filters, isLoggedIn) => {
  const query = qs.stringify(
    {
      ...filters,
    },
    {
      encodeValuesOnly: true,
    },
  );

  if (isLoggedIn) {
    const { data } = await api.get(`/ranking-statistics-resume?${query}`);
    return data;
  } else {
    const { data } = await publicApi.get(`/ranking-statistics-resume?${query}`);
    return data;
  }
};

export const findRankItemHistory = async ({
  institutionId,
  market,
  type,
  criteria,
  timeRange,
}) => {
  const query = qs.stringify(
    {
      populate: {
        rank: {
          fields: ['baseYear', 'baseMonth'],
        },
        criteria: {
          fields: ['label'],
        },
      },
      sort: ['rank.baseYear:desc'],
      filters: {
        institution: {
          id: institutionId,
        },
        criteria: {
          slug: criteria,
        },
        rank: {
          market: {
            name: market,
          },
          type,
          timeRange,
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const {
    data: { data },
  } = await api.get(`/rank-items?${query}`);

  return data;
};

export const requestRanking = async (formData) => {
  const { data } = await api.post('/solicitacoes-rankings', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });

  return data;
};

export const findInstitutionRankingCount = async (institutionId) => {
  const query = qs.stringify(
    {
      filters: {
        institution: {
          id: institutionId,
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/rank-item-count?${query}`);

  return data;
};

export const findRankingToHome = async () => {
  return publicApi.get('/rankings-home');
};
